import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { connect } from 'react-redux';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { createResourceLocatorString } from '../../util/routes';

/**
 * AdminRoleGuard - Higher-Order Component for role-based access control
 *
 * This component wraps admin pages and enforces role-based authorization.
 * If the user doesn't have the required role, they are redirected to NotFoundPage.
 *
 * @param {string|string[]} requiredRoles - Single role or array of roles required to access the page
 * @returns {Function} HOC function that wraps components
 */
const withAdminRoleGuard = requiredRoles => WrappedComponent => {
  const AdminRoleGuardComponent = props => {
    const { currentUser } = props;
    const history = useHistory();
    const routeConfiguration = useRouteConfiguration();

    useEffect(() => {
      // Wait for user data to load
      if (!currentUser) {
        return;
      }

      const privateData = currentUser?.attributes?.profile?.privateData || {};

      // Normalize requiredRoles to array
      const rolesArray = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];

      // Check if user has at least one of the required roles
      const hasRequiredRole = rolesArray.some(role => {
        switch (role) {
          case 'isOrganizationAdmin':
            return privateData.isOrganizationAdmin === true;
          case 'isSupabaseAdmin':
            return privateData.isSupabaseAdmin === true;
          case 'isModAdmin':
            return privateData.isModAdmin === true;
          case 'isWalletAdmin':
            return privateData.isWalletAdmin === true;
          case 'isUserAdmin':
            return privateData.isUserAdmin === true;
          case 'isCategoryAdmin':
            return privateData.isCategoryAdmin === true;
          case 'isHighlightAdmin':
            return privateData.isHighlightAdmin === true;
          case 'isDiscountAdmin':
            return privateData.isDiscountAdmin === true;
          case 'isDailyDealsAdmin':
            return privateData.isDailyDealsAdmin === true;
          case 'isSubscriptionAdmin':
            return privateData.isSubscriptionAdmin === true;
          case 'isDiscoverGroupsCarouselAdmin':
            return privateData.isDiscoverGroupsCarouselAdmin === true;
          default:
            return false;
        }
      });

      // Redirect to NotFoundPage if user doesn't have required role
      if (!hasRequiredRole) {
        history.replace(createResourceLocatorString('NotFoundPage', routeConfiguration, {}, {}));
      }
    }, [currentUser, history, routeConfiguration]);

    // Don't render the component if user data hasn't loaded yet
    if (!currentUser) {
      return null;
    }

    const privateData = currentUser?.attributes?.profile?.privateData || {};
    const rolesArray = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
    const hasRequiredRole = rolesArray.some(role => privateData[role] === true);

    // Don't render if user doesn't have required role
    if (!hasRequiredRole) {
      return null;
    }

    // Render the wrapped component if authorized
    return <WrappedComponent {...props} />;
  };

  // Set display name for debugging
  AdminRoleGuardComponent.displayName = `withAdminRoleGuard(${WrappedComponent.displayName ||
    WrappedComponent.name ||
    'Component'})`;

  // Connect to Redux to get currentUser
  const mapStateToProps = state => ({
    currentUser: state.user.currentUser,
  });

  return connect(mapStateToProps)(AdminRoleGuardComponent);
};

export default withAdminRoleGuard;
